import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { addTransaction, updateTransaction } from '../firebase/firestoreService';
import { formatDateMMDDYYYY, toMMDDYYYY } from '../utils/dateFormat';
import { formatCurrency } from '../utils/formatCurrency';
import Toast from './Toast';
import TransactionModal from './TransactionModal';
import AllTransactionsModal from './AllTransactionsModal';

function StoreCard({ store, onUpdate, personId }) {
  const [isEditing, setIsEditing] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [firestoreTransactions, setFirestoreTransactions] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editData, setEditData] = useState({
    name: store.name || `Store #${store.num}`,
    bal: store.bal
  });

  // Firestore'dan transaction'ları çek (Real-time)
  useEffect(() => {
    console.log('[StoreCard] useEffect triggered', { personId, storeCode: store.storeCode });
    
    if (!personId || !store.storeCode) {
      console.log('[StoreCard] Missing personId or storeCode', { personId, storeCode: store.storeCode });
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      console.log('[StoreCard] No authenticated user');
      return;
    }

    console.log('[StoreCard] Setting up Firestore listener for:', { personId, storeCode: store.storeCode });

    // people/{personId}/transactions collection'ından storeCode ile filtrele
    const transactionsRef = collection(db, 'people', personId, 'transactions');
    const q = query(
      transactionsRef,
      where('storeCode', '==', store.storeCode)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('[StoreCard] Firestore snapshot received', { 
        docsCount: snapshot.docs.length,
        personId,
        storeCode: store.storeCode 
      });
      
      const txs = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('[StoreCard] Transaction doc:', { id: doc.id, data });
        const dateVal = data.date?.toDate ? data.date.toDate() : new Date();
        return {
          id: doc.id,
          type: data.type,
          amount: data.amount,
          desc: data.description || '',
          date: formatDateMMDDYYYY(dateVal),
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date()
        };
      });
      
      // Client-side'da sırala (createdAt'a göre desc)
      txs.sort((a, b) => b.createdAt - a.createdAt);
      
      console.log('[StoreCard] Setting firestoreTransactions:', txs);
      setFirestoreTransactions(txs);
    }, (error) => {
      console.error('[StoreCard] Firestore error:', error);
    });

    return () => {
      console.log('[StoreCard] Cleaning up listener');
      unsubscribe();
    };
  }, [personId, store.storeCode]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setShowToast(true);
  };

  const handleSave = () => {
    onUpdate(store.num, {
      ...editData,
      bal: parseFloat(editData.bal) || 0
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      name: store.name || `Store #${store.num}`,
      bal: store.bal
    });
    setIsEditing(false);
  };

  const handleAddTransaction = async (transaction) => {
    if (editingTransaction) {
      // Update existing transaction
      await updateTransaction(
        personId, 
        editingTransaction.id, 
        store.storeCode, 
        editingTransaction, 
        transaction
      );
      setEditingTransaction(null);
    } else {
      // Add new transaction
      await addTransaction(personId, store.storeCode, store.name || store.storeCode, transaction);
    }
  };

  const handleTransactionClick = (transaction) => {
    // Only allow editing Firestore transactions (not local ones)
    if (transaction.id && typeof transaction.id === 'string') {
      setEditingTransaction(transaction);
      setShowTransactionModal(true);
    }
  };

  if (isEditing) {
    return (
      <div className="st st-editing">
        <div className="st-edit-header">
          <input
            type="text"
            className="st-edit-name"
            value={editData.name}
            onChange={(e) => setEditData({...editData, name: e.target.value})}
            placeholder="Store Name"
          />
          <input
            type="number"
            step="0.01"
            className="st-edit-bal"
            value={editData.bal}
            onChange={(e) => setEditData({...editData, bal: e.target.value})}
            placeholder="Balance"
          />
        </div>
        <div className="st-edit-actions">
          <button className="st-btn-cancel" onClick={handleCancel}>✕ Cancel</button>
          <button className="st-btn-save" onClick={handleSave}>✓ Save</button>
        </div>
      </div>
    );
  }

  // Firestore + Local transactions birleştir ve sırala (en yeni üstte)
  const localTransactions = (store.transactions || []).map(t => ({
    ...t,
    createdAt: t.createdAt || new Date()
  }));
  
  const allTransactions = [...firestoreTransactions, ...localTransactions];
  
  // En yeni transaction'lar üstte olsun (createdAt'a göre desc)
  allTransactions.sort((a, b) => {
    const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
    const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
    return dateB - dateA;
  });
  
  const recentTransactions = allTransactions.slice(0, 3);

  return (
    <>
      <div className="st">
        <div className="st-hd">
          <div className="st-num">{store.name || `Store #${store.num}`}</div>
          <div className="st-bal">${(store.bal || 0).toLocaleString()}</div>
          <button className="st-edit-btn" onClick={() => setIsEditing(true)}>
            ✏️
          </button>
        </div>
        <div className="st-f">
          <div className="st-l">📧 Email</div>
          <div className="st-v">
            <span>{store.email}</span>
            <button className="st-cp" onClick={() => copyToClipboard(store.email)}>
              📋
            </button>
          </div>
        </div>
        <div className="st-f">
          <div className="st-l">🔑 Password</div>
          <div className="st-v pass">
            <span>{store.pass}</span>
            <button className="st-cp" onClick={() => copyToClipboard(store.pass)}>
              📋
            </button>
          </div>
        </div>

        {/* Transactions Section */}
        <div className="store-transactions">
          <div className="transactions-header">
            <div className="transactions-title">📊 Transactions</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {allTransactions.length > 3 && (
                <button 
                  className="btn-view-all"
                  onClick={() => setShowAllTransactions(true)}
                >
                  View All ({allTransactions.length})
                </button>
              )}
              <button 
                className="btn-add-transaction"
                onClick={() => setShowTransactionModal(true)}
              >
                ➕ Add
              </button>
            </div>
          </div>

          {recentTransactions.length > 0 ? (
            recentTransactions.map(t => (
              <div 
                key={t.id} 
                className="transaction-item"
                onClick={() => handleTransactionClick(t)}
                style={{ cursor: typeof t.id === 'string' ? 'pointer' : 'default' }}
                title={typeof t.id === 'string' ? 'Click to edit' : ''}
              >
                <span className="transaction-desc">{t.desc}</span>
                <span className={`transaction-amount ${t.type}`}>
                  {t.type === 'income' ? '+' : '-'}${formatCurrency(Math.abs(t.amount))}
                </span>
                <span className="transaction-date">{toMMDDYYYY(t.date) || t.date}</span>
              </div>
            ))
          ) : (
            <div className="no-transactions">No transactions yet</div>
          )}
        </div>
      </div>

      {showToast && <Toast message="Copied to clipboard!" onClose={() => setShowToast(false)} />}
      {showTransactionModal && (
        <TransactionModal
          store={store}
          onClose={() => {
            setShowTransactionModal(false);
            setEditingTransaction(null);
          }}
          onSave={handleAddTransaction}
          existingTransaction={editingTransaction}
        />
      )}
      {showAllTransactions && (
        <AllTransactionsModal
          store={{ ...store, transactions: allTransactions }}
          onClose={() => setShowAllTransactions(false)}
        />
      )}
    </>
  );
}

export default StoreCard;